import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { referralShareUrl } from './publicFormUrl';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL });
API.interceptors.request.use(config => {
  const token = localStorage.getItem('cms_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const emptyForm = {
  code: '',
  partnerName: '',
  community: '',
  partnerEmail: '',
  formId: ''
};

function generateCode(name = '') {
  const letters = String(name).toUpperCase().replace(/[^A-Z]/g, '').replace(/[O]/g, 'K').slice(0, 4) || 'SI3';
  const digits = String(Math.floor(10 + Math.random() * 89));
  return `${letters}${digits}`.slice(0, 12);
}

export default function ReferralsManager({
  project,
  forms = [],
  userRole,
  formId = null,
  lockedFormId = false,
  compact = false
}) {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({ ...emptyForm, formId: formId || '' });
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState('');

  const isViewOnly = userRole === 'viewer';
  const effectiveFormId = lockedFormId ? formId : (draft.formId || formId || '');

  const load = async () => {
    if (!project?._id) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ projectId: project._id });
      if (formId) params.set('formId', formId);
      const res = await API.get(`/api/referrals?${params.toString()}`);
      setReferrals(res.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [project?._id, formId]);

  useEffect(() => {
    if (formId) setDraft(d => ({ ...d, formId }));
  }, [formId]);

  const handleCreate = async () => {
    if (!draft.code.trim()) return;
    setSaving(true);
    setError('');
    try {
      await API.post('/api/referrals', {
        projectId: project._id,
        code: draft.code.trim(),
        partnerName: draft.partnerName.trim(),
        community: draft.community.trim(),
        partnerEmail: draft.partnerEmail.trim(),
        label: draft.community.trim() || draft.partnerName.trim(),
        formId: effectiveFormId || null
      });
      setDraft({ ...emptyForm, formId: formId || '' });
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setSaving(false);
  };

  const toggleStatus = async (referral) => {
    try {
      await API.put(`/api/referrals/${referral._id}`, {
        status: referral.status === 'active' ? 'paused' : 'active'
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this referral code?')) return;
    try {
      await API.delete(`/api/referrals/${id}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    }
  };

  const formFor = (id) => forms.find(x => x._id === id);
  const formTitle = (id) => {
    if (!id) return 'All forms';
    return formFor(id)?.title || 'Form';
  };
  const linkFor = (referral) => {
    if (referral.shareUrl) return referral.shareUrl;
    const f = formFor(referral.formId) || (formId ? formFor(formId) : null);
    const slug = f?.slug || (formId && forms.find(x => x._id === formId)?.slug) || 'siherdefi-application';
    return referralShareUrl(slug, referral.code);
  };

  const copyLink = async (referral) => {
    const url = linkFor(referral);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(referral._id);
      setTimeout(() => setCopiedId(''), 1600);
    } catch {
      window.prompt('Copy referral link:', url);
    }
  };

  return (
    <div style={{ maxWidth: compact ? '100%' : 1100, padding: compact ? '8px 0 0' : 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {!compact && (
        <div>
          <h3 style={{ margin: '0 0 6px 0' }}>Forms + Referrals</h3>
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 13 }}>
            Create partner codes, copy shareable links, and track clicks / starts / submissions.
            Share links open the public form at <code>si3.space/&#123;slug&#125;?ref=CODE</code>.
          </p>
        </div>
      )}

      {compact && (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Referral codes for this form</h3>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>{referrals.length} code{referrals.length === 1 ? '' : 's'}</span>
        </div>
      )}

      {error && (
        <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(239,68,68,.12)', color: '#fca5a5', fontSize: 13 }}>
          {error}
        </div>
      )}

      {!isViewOnly && (
        <div style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg)', display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 500 }}>Code *</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input className="minput" value={draft.code} onChange={e => setDraft(d => ({ ...d, code: e.target.value }))} placeholder="e.g. KARATEST" />
              <button
                type="button"
                className="btn-ghost"
                style={{ fontSize: 11, padding: '6px 8px', whiteSpace: 'nowrap' }}
                onClick={() => setDraft(d => ({ ...d, code: generateCode(d.partnerName) }))}
              >
                Regenerate
              </button>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 500 }}>Partner name *</label>
            <input className="minput" value={draft.partnerName} onChange={e => setDraft(d => ({ ...d, partnerName: e.target.value }))} placeholder="Contact name" />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 500 }}>Community / project *</label>
            <input className="minput" value={draft.community} onChange={e => setDraft(d => ({ ...d, community: e.target.value }))} placeholder="Community name" />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 500 }}>Partner email *</label>
            <input className="minput" type="email" value={draft.partnerEmail} onChange={e => setDraft(d => ({ ...d, partnerEmail: e.target.value }))} placeholder="partner@example.com" />
          </div>
          {!lockedFormId && (
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 500 }}>Form</label>
              <select className="minput" value={draft.formId} onChange={e => setDraft(d => ({ ...d, formId: e.target.value }))}>
                <option value="">All forms</option>
                {forms.map(f => (
                  <option key={f._id} value={f._id}>{f.title}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'end' }}>
            <button
              className="btn-solid"
              onClick={handleCreate}
              disabled={saving || !draft.code.trim() || !draft.partnerName.trim() || !draft.community.trim() || !draft.partnerEmail.trim()}
            >
              {saving ? 'Saving…' : '+ Add partner code'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading referrals…</div>
      ) : referrals.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)', border: '1px dashed var(--border)', borderRadius: 8 }}>
          No referral codes yet for this form
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', background: 'var(--bg)' }}>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>Code</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>Referral link</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>Partner</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>Community</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>Email</th>
                {!formId && <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>Form</th>}
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>Status</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>Clicks</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>Starts</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>Submits</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>Conv %</th>
                <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}></th>
              </tr>
            </thead>
            <tbody>
              {referrals.map(r => (
                <tr key={r._id}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', fontWeight: 600 }}>{r.code}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', maxWidth: 260 }}>
                    {(() => {
                      const url = linkFor(r);
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <a href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', wordBreak: 'break-all', fontSize: 12 }}>
                            {url}
                          </a>
                          <button className="btn-ghost" style={{ fontSize: 11, padding: '2px 8px', width: 'fit-content' }} onClick={() => copyLink(r)}>
                            {copiedId === r._id ? 'Copied' : 'Copy link'}
                          </button>
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{r.partnerName || r.label || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{r.community || '—'}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{r.partnerEmail || '—'}</td>
                  {!formId && <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{formTitle(r.formId)}</td>}
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: r.status === 'active' ? 'rgba(16,185,129,.15)' : 'rgba(148,163,184,.15)',
                      color: r.status === 'active' ? '#34d399' : '#94a3b8'
                    }}>{r.status}</span>
                  </td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{r.clicks || 0}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{r.starts || 0}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{r.submissions || 0}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{r.conversionRate ?? 0}%</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    {!isViewOnly && (
                      <>
                        <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 8px', marginRight: 6 }} onClick={() => toggleStatus(r)}>
                          {r.status === 'active' ? 'Pause' : 'Activate'}
                        </button>
                        <button className="btn-ghost" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => handleDelete(r._id)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
